import React, {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Vehicle } from '@app/models';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Loader } from '@app/components';
import { VehicleInfo } from '@app/common';
import { deleteVehicle, getVehicleById } from '@app/api';
import { ModalConfirm } from '@app/modals';
import { IconBin } from '@app/icons';

interface Props {}

function PageProfileVehicle(props: Props) {
  const { id } = useParams<'id'>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [pending, setPending] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [isDeleted, setIsDeleted] = useState<boolean>(false);

  const getData = useCallback(async () => {
    try {
      const vehicleResponse = await getVehicleById(id as string);

      setVehicle(vehicleResponse);
      setError(false);
      setPending(false);
    } catch (e) {
      setError(true);
      setPending(false);
    }
  }, [id]);

  const onEditVehicle = useCallback(() => {
    navigate(`/profile/vehicle/${id}/edit`);
  }, [id, navigate]);

  const onClickDelete = useCallback(async () => {
    try {
      if (vehicle?.id) {
        await deleteVehicle(vehicle.id);
        setIsDeleted((prevState) => !prevState);
      }
    } catch (e) {}
  }, [vehicle?.id]);

  const onClickDeleteModal = useCallback(() => {
    setDeleteModalVisible(true);
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    setDeleteModalVisible(false);
  }, []);

  useEffect(() => {
    getData();
  }, []);

  const actionButtons = useMemo<ReactNode[]>(() => {
    if (!vehicle) {
      return [];
    }

    const returnActionButtons = [];
    returnActionButtons.push(
      <Button
        text=""
        icon={IconBin}
        onClick={onClickDeleteModal}
        variant={isDeleted ? 'contained' : 'outlined'}
      />
    );
    returnActionButtons.push(
      <Button text="Редактировать" onClick={onEditVehicle} />
    );

    return [...returnActionButtons];
  }, [isDeleted, onClickDeleteModal, onEditVehicle, vehicle]);

  if (pending) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!vehicle || error) {
    return null;
  }

  return (
    <>
      <VehicleInfo vehicle={vehicle} actions={actionButtons} editable={false} />
      <ModalConfirm
        textSuccess={'Успешно удален!'}
        onAccept={onClickDelete}
        title={'Вы уверены, что хотите удалить?'}
        visible={deleteModalVisible}
        onClose={onCloseDeleteModal}
      />
    </>
  );
}

export default memo(PageProfileVehicle);
